import { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";

import { useJwtRefresh } from "../../hooks/useJwtRefresh";
import { fetchConversationHistory } from "../../utils/chatApi";
import type { Conversation } from "../../utils/types";
import { RootState } from "src/redux/reducers";

export function useConversationHistory() {
  const { getValidToken } = useJwtRefresh();
  const chatwootId = useSelector(
    (state: RootState) => state.mainReducer.identity,
  )?.chatwootId?.id;
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Keep the latest token getter in a ref so it can be used inside the effect
  // without being an effect dependency. Otherwise the history would refetch
  // every time `getValidToken` is recreated (e.g. on every JWT refresh).
  const getValidTokenRef = useRef(getValidToken);
  getValidTokenRef.current = getValidToken;

  const fetchConversations = useCallback(
    async (signal: AbortSignal) => {
      if (!chatwootId) return;
      setIsLoading(true);
      const jwtToken = await getValidTokenRef.current();
      if (!jwtToken) {
        throw new Error("No valid token");
      }

      fetchConversationHistory(chatwootId, signal, jwtToken)
        .then(setConversations)
        .catch((error) => {
          if (error instanceof DOMException && error.name === "AbortError")
            return;
          console.error("Failed to fetch created conversations:", error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [chatwootId],
  );

  useEffect(() => {
    if (!chatwootId) return;
    const controller = new AbortController();

    fetchConversations(controller.signal);

    return () => controller.abort();
  }, [chatwootId, fetchConversations]);

  const refetchConversations = () => {
    const controller = new AbortController();
    fetchConversations(controller.signal);
  };

  return { conversations, isLoading, refetchConversations };
}
